import * as yup from 'yup';
import { OrderStatus } from './orderStatus';

// France country GUID — shared between schema validation and component UI
export const FRANCE_ID = '37d328c5-2ee9-4581-1ceb-08dc12ef16d3';

/**
 * Yup validation schema for the SemenCheckout form.
 *
 * Conditional rules (matching Flutter):
 * - Mare fields: required only when isRegisteredMare = false
 * - Covering location: required only when isForExport = true
 * - Accept contract: always required
 *
 * NOTE: Racing results validation is handled separately in onSubmit
 * since rows live outside React Hook Form (local state array).
 *
 * @param {function} translate - Translation function from useTranslate()
 */
export const buildSchema = (translate) =>
    yup.object().shape({
        isRegisteredMare: yup.boolean().nullable().required(translate('web.requiredField', 'Required Field')),
        isForExport: yup.boolean().nullable().required(translate('web.requiredField', 'Required Field')),
        semenType: yup.string().nullable().required(translate('web.requiredField', 'Required Field')),

        // Mare select — required when registered mare
        mareId: yup.string().nullable().when('isRegisteredMare', {
            is: true,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),

        // Mare name fields — all required when unregistered mare
        mareNameEn: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        mareNameAr: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        sireNameEn: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        sireNameAr: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        damNameEn: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        damNameAr: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        mareBirthDate: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        colorId: yup.string().nullable().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        originId: yup.string().nullable().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),

        mareRegisterNumber: yup.string().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),
        marePassport: yup.mixed().nullable().when('isRegisteredMare', {
            is: false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),

        // Owner name fields — required when isMareOwner is checked (and mare is unregistered)
        ownerNameEn: yup.string().when(['isRegisteredMare', 'isMareOwner'], {
            is: (isReg, isOwner) => isReg === false && isOwner === false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),
        ownerNameAr: yup.string().when(['isRegisteredMare', 'isMareOwner'], {
            is: (isReg, isOwner) => isReg === false && isOwner === false,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),

        // Covering location — required only when isForExport = true
        coveringCountryId: yup.string().nullable().when('isForExport', {
            is: true,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        // Center name/number — required when France is selected
        centerName: yup.string().when('coveringCountryId', {
            is: (val) => val === FRANCE_ID,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),
        centerNumber: yup.string().when('coveringCountryId', {
            is: (val) => val === FRANCE_ID,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),

        // Accept contract for pending payment orders (and new orders if they somehow show it)
        acceptContract: yup.boolean().when('orderStatus', {
            is: (val) => val === OrderStatus.PendingPayment,
            then: (s) => s.oneOf([true], translate('web.requiredField', 'Required Field')),
            otherwise: (s) => s.nullable(),
        }),

        // Payment documents
        paymentCertificate: yup.mixed().nullable().when('orderStatus', {
            is: (val) => val === OrderStatus.PendingPayment,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),
        signedContract: yup.mixed().nullable().when('orderStatus', {
            is: (val) => val === OrderStatus.PendingPayment,
            then: (s) => s.required(translate('web.requiredField', 'Required Field')),
        }),

        // Racing Results — required when isBestHorse is true
        racingResults: yup.array().of(
            yup.object().shape({
                raceName: yup.string().nullable(),
                classification: yup.string().nullable(),
                raceDate: yup.string().nullable(),
                rank: yup.mixed().nullable(),
            })
        ).when('isBestHorse', {
            is: (val) => String(val) === 'true',
            then: (s) => s.test(
                'racing-results-required',
                translate('web.bestHorseRacingResultsRequired', 'Mare Racing Results are required for this horse'),
                function (rows) {
                    if (!rows || rows.length === 0) return false;
                    // Check if at least one row is fully complete
                    return rows.some((r) => {
                        const rn = (r.raceName || '').trim();
                        const rc = (r.classification || '').trim();
                        const rd = (r.raceDate || '').trim();
                        const rk = String(r.rank || '').trim();
                        return rn && rc && rd && rk;
                    });
                }
            ),
        }),
    });

/**
 * Default form values for the SemenCheckout form.
 */
export const defaultFormValues = {
    isRegisteredMare: true,
    isForExport: false,
    mareId: '',
    mareNameEn: '',
    mareNameAr: '',
    sireNameEn: '',
    sireNameAr: '',
    damNameEn: '',
    damNameAr: '',
    mareBirthDate: '',
    colorId: null,
    originId: null,

    mareRegisterNumber: '',
    marePassport: null,
    isMareOwner: true,
    ownerNameEn: '',
    ownerNameAr: '',
    coveringCountryId: null,
    centerName: '',
    centerNumber: '',
    externalComment: '',
    orderStatus: '',
    paymentCertificate: null,
    signedContract: null,
    acceptContract: false,
    isBestHorse: false,
    semenType: null,
    racingResults: [{ raceName: '', classification: '', raceDate: '', rank: '' }],
};
